import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  getPublicShareMeta, getPublicSharePdfUrl,
} from '../utils/api';
import styles from './PublicShareViewer.module.css';

// Public, unauthenticated viewer for share links. Lives outside the rest
// of the app — no TopBar, no sign-in gate. Hits /api/share/:token to fetch
// metadata, then embeds the PDF stream from /api/share/:token/pdf.
//
// 410 / 404 from the metadata fetch are surfaced as a polite
// "this link is no longer available" page.

export default function PublicShareViewer({ token }) {
  const [meta, setMeta]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);
  // We fetch the PDF as a Blob and then hand a `blob:` URL to <object>.
  // Doing it this way (instead of just pointing the <object> at the API
  // path) sidesteps a CRA dev-server quirk: requests with
  // `Accept: text/html` (which is what an <object data="..."> navigation
  // sends) are NOT proxied to the backend by default — CRA serves the
  // SPA's index.html instead, and the iframe ends up rendering the app
  // again inside itself. axios uses XHR, which the proxy treats correctly,
  // so the bytes always come back as a real PDF.
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  useEffect(() => {
    let alive = true;
    getPublicShareMeta(token)
      .then((m) => { if (alive) { setMeta(m); setLoading(false); } })
      .catch((err) => { if (alive) { setError(err.message); setLoading(false); } });
    return () => { alive = false; };
  }, [token]);

  // Stream the PDF as a Blob the moment we know the link is valid.
  useEffect(() => {
    if (!meta) return;
    let alive = true;
    let createdUrl = null;
    axios.get(getPublicSharePdfUrl(token), { responseType: 'blob' })
      .then((res) => {
        if (!alive) return;
        // Some servers send 'application/octet-stream' even for PDFs; coerce.
        const blob = new Blob([res.data], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(createdUrl);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.error || err.message || 'Could not load PDF');
      });
    return () => {
      alive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [meta, token]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>Loading…</div>
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className={styles.page}>
        <div className={styles.errorWrap}>
          <h1 className={styles.errorTitle}>Link unavailable</h1>
          <p className={styles.errorText}>
            {error || 'This share link is no longer valid.'}
          </p>
          <p className={styles.errorHint}>
            Ask the project team for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand}>
          GUMBO<span className={styles.brandAccent}>GRID</span>
          <span className={styles.brandSep}>·</span>
          <span className={styles.brandLabel}>SHARED VIEW</span>
        </div>
        <div className={styles.meta}>
          {meta.project?.name && (
            <span className={styles.metaProject}>
              {meta.project.number ? `${meta.project.number} · ` : ''}
              {meta.project.name}
            </span>
          )}
          <span className={styles.metaSheet}>
            {meta.sheet?.sheet_number}
            {meta.sheet?.title && meta.sheet.title !== meta.sheet.sheet_number
              ? ` — ${meta.sheet.title}` : ''}
          </span>
        </div>
      </header>

      <div className={styles.viewerWrap}>
        {pdfBlobUrl ? (
          // Browsers render PDFs natively in <object>; <iframe> fallback
          // covers a few obscure builds. Both are pointed at the blob URL
          // so neither triggers a navigation through the dev-server proxy.
          <object data={pdfBlobUrl} type="application/pdf" className={styles.viewer}>
            <iframe src={pdfBlobUrl} title="Shared sheet" className={styles.viewer} />
          </object>
        ) : (
          <div className={styles.empty}>Loading PDF…</div>
        )}
      </div>

      <footer className={styles.footer}>
        Read-only public link · No edits, no markups.
      </footer>
    </div>
  );
}
